export interface ReviewContent {
  css: string;
  html: string;
  stylesheets: string[];
  inlineScripts: string[];
  externalScripts: string[];
}

export const REVIEW_SCOPE_CLASS = "review-scope";

const ABSOLUTE_URL = /^(?:[a-z][a-z0-9+.-]*:|\/\/|#)/i;

function resolveAsset(value: string, base: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (trimmed.startsWith("data:") || ABSOLUTE_URL.test(trimmed)) return trimmed;
  try {
    return new URL(trimmed, base).href;
  } catch {
    return trimmed;
  }
}

function stripCssComments(css: string): string {
  return css.replace(/\/\*[\s\S]*?\*\//g, "");
}

/**
 * Contractează un selector ca să nu iasă din containerul review-ului.
 * `body`, `html` și `:root` devin chiar containerul.
 */
function scopeSelector(selector: string, scope: string): string {
  return selector
    .split(",")
    .map((rawPart) => {
      let part = rawPart.trim();
      if (!part) return "";

      const lead = /^(?:html|body|:root)(?=\s|>|$)/i;
      while (lead.test(part)) {
        part = part.replace(lead, "").replace(/^\s*>\s*/, "").trim();
      }
      if (!part) return scope;

      if (part.startsWith("*")) {
        const rest = part.slice(1).trim();
        return rest ? `${scope} ${rest}` : scope;
      }
      return `${scope} ${part}`;
    })
    .filter(Boolean)
    .join(", ");
}

function walkCss(css: string, scope: string): string {
  let output = "";
  let index = 0;

  while (index < css.length) {
    const open = css.indexOf("{", index);
    if (open === -1) {
      output += css.slice(index);
      break;
    }

    const prelude = css.slice(index, open).trim();
    let depth = 1;
    let cursor = open + 1;
    while (cursor < css.length && depth > 0) {
      const char = css[cursor];
      if (char === "{") depth += 1;
      else if (char === "}") depth -= 1;
      cursor += 1;
    }

    const body = css.slice(open + 1, cursor - 1);

    if (/^@(media|supports|layer|container|scope)/i.test(prelude)) {
      output += `${prelude}{${walkCss(body, scope)}}`;
    } else if (prelude.startsWith("@")) {
      output += `${prelude}{${body}}`;
    } else {
      output += `${scopeSelector(prelude, scope)}{${body}}`;
    }

    index = cursor;
  }

  return output;
}

export function scopeReviewCss(css: string, scope: string): string {
  if (!css.trim()) return "";
  return walkCss(stripCssComments(css), scope);
}

/**
 * Separă pagina scrisă de utilizator în bucăți reutilizabile:
 * CSS (contractat), HTML (fără scripturi) și scripturile, care rulează
 * în pagina principală ca să aibă acces la `window.ethereum`.
 */
export function parseReviewDocument(raw: string, baseUrl: string): ReviewContent {
  const doc = new DOMParser().parseFromString(raw, "text/html");

  const cssParts: string[] = [];
  doc.querySelectorAll("style").forEach((node) => {
    const text = node.textContent || "";
    if (text.trim()) cssParts.push(text);
  });

  const stylesheets: string[] = [];
  doc.querySelectorAll('link[rel="stylesheet"][href]').forEach((node) => {
    const href = node.getAttribute("href");
    if (href) stylesheets.push(resolveAsset(href, baseUrl));
  });

  const externalScripts: string[] = [];
  doc.querySelectorAll("script[src]").forEach((node) => {
    const src = node.getAttribute("src");
    if (src) externalScripts.push(resolveAsset(src, baseUrl));
  });

  const inlineScripts: string[] = [];
  doc.querySelectorAll("script").forEach((node) => {
    if (node.hasAttribute("src")) return;
    const text = node.textContent || "";
    if (text.trim()) inlineScripts.push(text);
  });

  doc.querySelectorAll("script").forEach((node) => node.remove());

  doc.querySelectorAll("[src]").forEach((node) => {
    const src = node.getAttribute("src");
    if (src) node.setAttribute("src", resolveAsset(src, baseUrl));
  });
  doc.querySelectorAll("a[href]").forEach((node) => {
    const href = node.getAttribute("href");
    if (href) node.setAttribute("href", resolveAsset(href, baseUrl));
  });
  doc.querySelectorAll("[poster]").forEach((node) => {
    const poster = node.getAttribute("poster");
    if (poster) node.setAttribute("poster", resolveAsset(poster, baseUrl));
  });

  return {
    css: cssParts.join("\n"),
    html: doc.body ? doc.body.innerHTML : "",
    stylesheets,
    inlineScripts,
    externalScripts,
  };
}

/** Aplicat pe documentul încărcat - nu vrem să afișăm aplicația ca review. */
export function isAppDocument(doc: Document): boolean {
  return (
    Boolean(doc.getElementById("root")) ||
    Boolean(doc.querySelector('script[src*="/src/main"]'))
  );
}